// View renderers for the Value Investing Rules v2 UI — white minimalist theme.

import type {
  InvestorAnalysis,
  RuleSet,
  RuleResult,
  AllScoresResponse,
  TracksResponse,
  TrackScore,
  RiskOverlay,
  DimensionScore,
  MetricRow,
  DisqualifiersResponse,
  DisqualifiersSummary,
  DisqualifierCheck,
  GateResult,
  GateCheck,
  TopStocksResponse,
  TopStock,
  Band,
  SellRule,
  PositionSizing,
} from "./api";
import { INVESTOR_SLUGS } from "./api";

const TRACK_ORDER = ["deep_value", "quality_compounder"];

/* Strategy metadata — we surface the STRATEGY name (not the investor's name) to
   avoid implying any endorsement. */
interface StrategyMeta { name: string; tag: string; icon: string; }
const STRATEGY: Record<string, StrategyMeta> = {
  graham: { name: "Classic Value", tag: "Below intrinsic & liquidation value", icon: "fa-gem" },
  schloss: { name: "Statistical Bargains", tag: "Deep discount to book, diversified", icon: "fa-layer-group" },
  klarman: { name: "Margin of Safety", tag: "Capital preservation first", icon: "fa-shield-halved" },
  burry: { name: "Forensic Deep Value", tag: "Hard cash metrics, footnote forensics", icon: "fa-magnifying-glass-chart" },
  pabrai: { name: "Asymmetric Bets", tag: "Low risk, high uncertainty", icon: "fa-scale-unbalanced" },
  buffett: { name: "Quality & Moats", tag: "Owner earnings & durable moats", icon: "fa-chess-king" },
  munger: { name: "High-ROIC Quality", tag: "Compounding at high returns on capital", icon: "fa-brain" },
  greenblatt: { name: "Magic Formula", tag: "Earnings yield × return on capital", icon: "fa-wand-magic-sparkles" },
  lynch: { name: "Growth at a Reasonable Price", tag: "PEG / GARP", icon: "fa-arrow-trend-up" },
  marks: { name: "Risk & Cycles", tag: "Downside protection & cycle awareness", icon: "fa-arrows-to-circle" },
};

const INVESTORS_BY_TRACK: Record<string, string[]> = {
  deep_value: ["graham", "schloss", "klarman", "burry", "pabrai"],
  quality_compounder: ["buffett", "munger", "greenblatt", "lynch"],
};

function strategyOf(id: string): StrategyMeta {
  return STRATEGY[id] || { name: id, tag: "", icon: "fa-circle" };
}

/* ------------------------------------------------------------------ */
/* formatting helpers                                                  */
/* ------------------------------------------------------------------ */
function escapeHtml(s: any): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function formatNumber(num: number | null | undefined, decimals = 2): string {
  if (num === null || num === undefined) return "—";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Current market drives currency formatting. India money is INR crore; US money
// is USD millions (as stored in market_cap_cr). Kept as module state and updated
// by setViewMarket() when the user switches markets.
let viewMarket: "india" | "us" = "india";
export function setViewMarket(market: "india" | "us"): void {
  viewMarket = market === "us" ? "us" : "india";
}

function formatCurrency(value: number | string | null | undefined): string {
  const v = typeof value === "string" ? parseFloat(value) : value;
  if (v === null || v === undefined || isNaN(v as number)) return "N/A";
  const n = v as number;
  if (viewMarket === "us") {
    // value is in USD millions
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}T`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}B`;
    return `$${n.toFixed(0)}M`;
  }
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L Cr`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(2)}K Cr`;
  return `₹${n.toFixed(0)} Cr`;
}

function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return "—";
  return score.toFixed(1);
}

function formatRuleValue(value: number | Record<string, any> | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return formatNumber(value);
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([k, v]) => `${escapeHtml(k)}: ${typeof v === "number" ? formatNumber(v) : escapeHtml(v)}`)
      .join(" · ");
  }
  return escapeHtml(value);
}

/* colour system — green / blue(watch) / grey / red. No orange. */
function bandKey(colour?: string): "green" | "blue" | "red" | "grey" {
  switch ((colour || "").toLowerCase()) {
    case "green": return "green";
    case "amber": return "blue"; // "watch" renders blue, never orange
    case "red": return "red";
    default: return "grey";
  }
}
const SHORT_BAND: Record<string, string> = {
  strong: "Strong", watch: "Watchlist", reject: "Reject",
  possible_trap: "Value trap", unscored: "Not scored",
};
function shortBand(band?: Band): string {
  if (!band) return "";
  return SHORT_BAND[band.id] || band.label || band.id;
}

/* subtle staggered entrance */
function anim(delay = 0): string {
  return `class="anim-in" style="animation-delay:${delay}ms"`;
}

function disclaimerBox(): string {
  return `
    <div class="disclaimer" ${anim(40)}>
      <i class="fas fa-circle-info"></i>
      <span><strong>Educational tool only.</strong> Independent study of publicly available investment methodologies. Not endorsed by anyone and not financial advice.</span>
    </div>`;
}

function dataFlagsFootnote(flags?: string[]): string {
  if (!flags || flags.length === 0) return "";
  return `
    <div class="data-flags" ${anim(80)}>
      <div class="data-flags-title">Data approximations</div>
      <ul>${flags.map((f) => `<li>${escapeHtml(f)}</li>`).join("")}</ul>
    </div>`;
}

function renderStockInfo(data: {
  ticker: string; company_name?: string; market_cap_cr?: number | string;
  sector?: string; industry?: string; data_date?: string;
}): string {
  const company = data.company_name && data.company_name !== "N/A" ? data.company_name : "";
  let dataDate = "";
  if (data.data_date) {
    try {
      dataDate = new Date(data.data_date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
    } catch (_) { dataDate = data.data_date; }
  }
  const [avBg, avFg] = avatarColours(data.ticker);
  const sector = data.sector && data.sector !== "N/A" ? data.sector : "";
  return `
    <div class="stock-info-card" ${anim(0)}>
      <div class="stock-info-main">
        <div class="stock-avatar" style="background:${avBg};color:${avFg};">${escapeHtml(data.ticker.slice(0, 2))}</div>
        <div>
          ${company ? `<div class="stock-info-name">${escapeHtml(company)}</div>` : ""}
          <div class="stock-info-ticker">${escapeHtml(data.ticker)}
            ${sector ? `<span class="tsc-badge tsc-badge-soft"><i class="fas fa-tag"></i> ${escapeHtml(sector)}</span>` : ""}
          </div>
        </div>
      </div>
      <div class="stock-info-meta">
        <div><span class="mini-label">Market Cap</span><span class="mini-value">${formatCurrency(data.market_cap_cr)}</span></div>
        ${dataDate ? `<div><span class="mini-label">As of</span><span class="mini-value">${dataDate}</span></div>` : ""}
      </div>
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Score gauge — chart-mount container; ApexCharts renders the solid    */
/* gauge into it after the HTML is inserted (see main.ts mountGauge).   */
/* ------------------------------------------------------------------ */
/* Shared colour used both by CSS classes (band-*, tint-*) and directly
   by chart code, keyed the same way as bandKey()'s output. */
export function chartColourFor(score: number | null | undefined): string {
  if (score === null || score === undefined) return "#94a3b8";
  if (score >= 7.5) return "#16a34a";
  if (score >= 5) return "#2563eb";
  return "#64748b";
}

function renderDonut(
  label: string,
  score: number | null | undefined,
  band: Band | undefined,
  subtitle: string,
  recommended: boolean,
  delay: number,
  chartId: string,
): string {
  const key = bandKey(band?.colour);
  const has = score !== null && score !== undefined;
  return `
    <div class="donut-card tint-${key} anim-in" style="animation-delay:${delay}ms">
      <span class="panel-strip strip-${key}"></span>
      ${recommended ? `<span class="donut-rec">Recommended</span>` : ""}
      <div class="donut-wrap">
        <div id="${chartId}" class="donut-chart-mount"></div>
        <div class="donut-max">${has ? "out of 10" : "not scored"}</div>
      </div>
      <div class="donut-label">${escapeHtml(label)}</div>
      ${band ? `<span class="band-chip band-${key}">${escapeHtml(shortBand(band))}</span>` : ""}
      <div class="donut-sub">${escapeHtml(subtitle)}</div>
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Shared band colour helper                                           */
/* ------------------------------------------------------------------ */
const PILL_HEX: Record<string, string> = {
  green: "#16a34a", blue: "#2563eb", red: "#dc2626", grey: "#64748b",
};

/* ------------------------------------------------------------------ */
/* Two-track scoring (three donut pies)                                */
/* ------------------------------------------------------------------ */
export function renderTrackScore(data: TracksResponse, investors?: AllScoresResponse["investors"]): string {
  const dv = data.tracks["deep_value"];
  const qc = data.tracks["quality_compounder"];
  const risk = data.risk_overlay;
  const recommended = data.recommended_track;

  const donuts = [
    dv ? renderDonut(dv.label || "Deep Value", dv.composite, dv.band, dv.suited_to || "Asset-heavy, out-of-favour names", dv.track === recommended, 60, "gauge-deep_value") : "",
    qc ? renderDonut(qc.label || "Quality Compounder", qc.composite, qc.band, qc.suited_to || "Durable franchises", qc.track === recommended, 140, "gauge-quality_compounder") : "",
    risk ? renderDonut(risk.label || "Risk Overlay", risk.score, risk.band, risk.suited_to || "Downside protection & risk control", false, 220, "gauge-risk") : "",
  ].join("");

  const percentileNote = data.percentiles_available
    ? ""
    : `<div class="inline-note" ${anim(30)}>Sector percentiles not built yet — open <strong>Top Stocks</strong> and Refresh to enable relative scoring.</div>`;

  const suppressed = data.sector_context?.suppressed_metrics || [];
  const sectorNote = data.sector_context?.override_key
    ? `<div class="inline-note" ${anim(0)}><strong>Sector overrides (${escapeHtml(data.sector_context.override_key)}):</strong> ${suppressed.length ? `metrics excluded — ${suppressed.map(escapeHtml).join(", ")}` : "applied"}.</div>`
    : "";

  const panels = [dv, qc].filter(Boolean).map((t, i) => renderTrackPanel(t as TrackScore, (t as TrackScore).track === recommended, 300 + i * 80)).join("");

  return `
    ${renderStockInfo(data)}
    ${renderScoreGlance()}
    ${renderStrategyShowcase(investors)}
    <div class="analysis-header" ${anim(30)}>
      <h2 class="analysis-title">Two-Track Value Score</h2>
      <p class="analysis-subtitle">Deep Value and Quality Compounder are scored independently — a stock qualifies on one or the other, never on the average. The Risk Overlay applies to both.</p>
    </div>
    ${percentileNote}
    <div class="donut-row">${donuts}</div>
    ${renderGates(data.gates)}
    ${panels}
    ${sectorNote}
    ${dataFlagsFootnote(data.data_flags)}
    ${disclaimerBox()}
    ${uiStyles()}
  `;
}

/* Compact "at a glance" summary — three separate ring gauges (Deep Value /
   Quality / Risk), each mounted individually, so the three scores can be
   read at a glance before drilling into strategies or the full breakdown
   below. */
function renderScoreGlance(): string {
  return `
    <div class="panel glance-panel anim-in" ${anim(10)}>
      <div class="panel-title glance-title">Score at a Glance</div>
      <div class="glance-rings">
        <div class="glance-ring-item">
          <div id="glance-ring-deep_value" class="glance-ring-mount"></div>
          <span class="glance-ring-label">Deep Value</span>
        </div>
        <div class="glance-ring-item">
          <div id="glance-ring-quality_compounder" class="glance-ring-mount"></div>
          <span class="glance-ring-label">Quality Compounder</span>
        </div>
        <div class="glance-ring-item">
          <div id="glance-ring-risk" class="glance-ring-mount"></div>
          <span class="glance-ring-label">Risk Overlay</span>
        </div>
      </div>
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Strategy showcase — every investor strategy, both tracks combined,   */
/* rendered as interactive cards (same visual language as Top Stocks).  */
/* ------------------------------------------------------------------ */
const TRACK_ACCENT: Record<string, string> = {
  deep_value: "#2563eb", quality_compounder: "#7c3aed",
};

/* Fallback display names, used only if the API investor payload is missing
   (e.g. the /score call failed) so the card can still show "<Name> style". */
const INVESTOR_DISPLAY_NAME: Record<string, string> = {
  graham: "Benjamin Graham", schloss: "Walter Schloss", klarman: "Seth Klarman",
  burry: "Michael Burry", pabrai: "Mohnish Pabrai", buffett: "Warren Buffett",
  munger: "Charlie Munger", greenblatt: "Joel Greenblatt", lynch: "Peter Lynch",
  marks: "Howard Marks",
};

function investorScoreOf(investors: AllScoresResponse["investors"] | undefined, id: string): number | null {
  const rec = investors?.[id];
  if (!rec || !rec.rule_sets || rec.rule_sets.length === 0) return null;
  const evaluable = rec.rule_sets.filter((rs) => rs.rules_evaluable > 0);
  if (evaluable.length === 0) return null;
  const avgPct = evaluable.reduce((sum, rs) => sum + rs.pass_rate, 0) / evaluable.length;
  return avgPct / 10; // pass_rate is 0-100 -> convert to an out-of-10 score
}

function renderStrategyShowcase(investors?: AllScoresResponse["investors"]): string {
  const entries = TRACK_ORDER.flatMap((trackId) =>
    (INVESTORS_BY_TRACK[trackId] || []).map((id) => ({ id, trackId })));
  const cards = entries.map(({ id, trackId }, i) => {
    const s = strategyOf(id);
    const slug = INVESTOR_SLUGS[id] || id;
    const accent = TRACK_ACCENT[trackId] || "#64748b";
    const investorName = investors?.[id]?.name || INVESTOR_DISPLAY_NAME[id] || "";
    const score = investorScoreOf(investors, id);
    const scoreKey = score === null ? "grey" : score >= 7.5 ? "green" : score >= 5 ? "blue" : "low";
    const scoreCls = scoreKey === "green" ? "dim-score-green" : scoreKey === "blue" ? "dim-score-blue" : scoreKey === "low" ? "dim-score-low" : "dim-score-null";
    return `
      <div class="tsc strategy-tsc anim-in" style="animation-delay:${Math.min(i, 10) * 30}ms" role="button" tabindex="0" onclick="navigateToInvestorAnalysis('${slug}')">
        <div class="tsc-labels"><span class="tsc-label" style="background:${accent}"></span></div>
        <div class="tsc-main">
          <div class="tsc-headline">
            <div class="tsc-title">${escapeHtml(s.name)}</div>
            ${investorName ? `<div class="tsc-investor">${escapeHtml(investorName)} style</div>` : ""}
            <div class="tsc-sub">${escapeHtml(s.tag)}</div>
          </div>
          <div class="tsc-score-mini dim-score ${scoreCls}" title="Rule pass rate for this strategy">${score === null ? "—" : `${score.toFixed(1)}/10`}</div>
        </div>
        <div class="tsc-foot">
          <div class="tsc-badges">
            <span class="tsc-badge" style="background:${accent};color:#fff">${escapeHtml(trackLabel(trackId))}</span>
            <span class="tsc-badge tsc-badge-soft"><i class="fas ${s.icon}"></i></span>
          </div>
          <span class="tsc-arrow">View full breakdown <i class="fas fa-arrow-right"></i></span>
        </div>
      </div>`;
  }).join("");
  return `
    <div class="analysis-header" ${anim(15)}>
      <h2 class="analysis-title">Explore Every Strategy</h2>
      <p class="analysis-subtitle">Pick a lens to see the full rule-by-rule alignment for this stock. The score is the share of that investor's evaluable rules this stock passes.</p>
    </div>
    <div class="ref-grid ts-grid strategy-showcase-grid">${cards}</div>`;
}

function renderTrackPanel(track: TrackScore, recommended: boolean, delay: number): string {
  const trap = track.band?.id === "possible_trap"
    ? `<div class="trap-banner"><i class="fas fa-triangle-exclamation"></i> Possible value trap — a weak dimension is usually why the stock is cheap. Run the checklist before proceeding.</div>`
    : "";
  const portfolio = track.expected_portfolio;
  const portfolioLine = portfolio && portfolio.positions
    ? `<span class="panel-portfolio">Typical: ${portfolio.positions[0]}–${portfolio.positions[1]} positions${portfolio.requires ? ` · ${escapeHtml(portfolio.requires)}` : ""}</span>`
    : "";

  const stripKey = bandKey(track.band?.colour);
  return `
    <div class="panel panel-track anim-in" style="animation-delay:${delay}ms">
      <div class="panel-band band-bg-${stripKey}">
        <div>
          <div class="panel-title">${escapeHtml(track.label || track.track)}${recommended ? ` <span class="rec-tag">Recommended</span>` : ""}</div>
          ${portfolioLine}
        </div>
        <div class="panel-band-right">
          ${track.band ? `<span class="band-chip band-${stripKey}">${escapeHtml(track.band.label || track.band.id || "")}</span>` : ""}
          <span class="panel-band-score" style="background:${PILL_HEX[stripKey]}">${track.composite !== null ? formatScore(track.composite) : "—"}</span>
        </div>
      </div>
      <div class="panel-body">
        ${trap}
        <div class="panel-dims-label">Score breakdown</div>
        <div id="stackbar-${track.track}" class="dim-bar-chart-mount"></div>
        <div class="dim-list">${track.dimensions.map(renderDimension).join("")}</div>
      </div>
    </div>`;
}

function renderDimension(dim: DimensionScore): string {
  const pct = dim.score !== null ? (dim.score / 10) * 100 : 0;
  const key = dim.score === null ? "null" : dim.score >= 7.5 ? "green" : dim.score >= 5 ? "blue" : "low";
  const barKey = key === "green" ? "green" : key === "blue" ? "blue" : "grey";
  return `
    <div class="dim">
      <div class="dim-head" onclick="toggleMetricsTable(this)">
        <div class="dim-info">
          <span class="dim-label">${escapeHtml(dim.label)}</span>
          <span class="dim-weight">${Math.round(dim.weight * 100)}% · ${dim.metrics_used}/${dim.metrics_total} metrics</span>
        </div>
        <div class="dim-right">
          <span class="dim-score dim-score-${key}">${formatScore(dim.score)}</span>
          <i class="fas fa-chevron-down expand-icon"></i>
        </div>
      </div>
      <div class="dim-bar"><div class="dim-bar-fill bar-${barKey}" style="width:${pct}%"></div></div>
      <div class="metrics-table-content collapsed">
        <table class="metrics-table compact">
          <thead><tr><th>Metric</th><th>Value</th><th>Score</th><th>Status</th></tr></thead>
          <tbody>${dim.metrics.map(renderMetricRow).join("")}</tbody>
        </table>
      </div>
    </div>`;
}

function renderMetricRow(m: MetricRow): string {
  let statusHtml: string;
  if (m.status === "ok") statusHtml = `<span class="chip chip-green">scored</span>`;
  else if (m.status === "suppressed") statusHtml = `<span class="chip chip-grey" title="${escapeHtml(m.reason || "sector override")}">n/a (sector)</span>`;
  else statusHtml = `<span class="chip chip-grey" title="${escapeHtml(m.reason || "data unavailable")}">not assessed</span>`;
  return `
    <tr>
      <td class="metric-rule">${escapeHtml(m.label || m.id)}</td>
      <td>${m.value === null ? "—" : formatRuleValue(m.value)}</td>
      <td>${m.score === null ? "—" : formatScore(m.score)}</td>
      <td>${statusHtml}</td>
    </tr>`;
}

function numsFrom(s?: string): number[] {
  return (s || "").match(/[\d.]+/g)?.map(Number) || [];
}

/* Plain-language sentence for each known gate; falls back to the raw detail
   text for any gate id we don't have a specific phrasing for. */
function gateSentence(c: GateCheck, dq: DisqualifiersSummary | undefined): { icon: string; tone: "ok" | "bad" | "na"; text: string } {
  const tone: "ok" | "bad" | "na" = c.passed === true ? "ok" : c.passed === false ? "bad" : "na";
  const icon = tone === "ok" ? "circle-check" : tone === "bad" ? "triangle-exclamation" : "circle-minus";

  if (c.id === "gate_disqualifiers") {
    if (tone === "ok") return { icon, tone, text: "No fraud or governance red flags turned up in the checks we could run." };
    if (tone === "bad") return { icon, tone, text: `Red flag${(dq?.hard_failures?.length || 0) > 1 ? "s" : ""} found: ${(dq?.hard_failures || []).map((f) => f.replace(/_/g, " ")).join(", ") || c.detail}.` };
    return { icon, tone, text: "Fraud and governance checks weren't assessed." };
  }
  if (c.id === "gate_size") {
    const [cap, min] = numsFrom(c.detail);
    const capStr = cap !== undefined ? formatCurrency(cap) : "unknown";
    const minStr = min !== undefined ? formatCurrency(min) : "the minimum";
    if (tone === "ok") return { icon, tone, text: `Large enough to invest in — market cap is ${capStr}, comfortably above the ${minStr} minimum.` };
    if (tone === "bad") return { icon, tone, text: `Too small for this screen — market cap is ${capStr}, below the ${minStr} minimum required.` };
    return { icon, tone, text: "Company size wasn't assessed." };
  }
  if (c.id === "gate_liquidity") {
    return { icon: "circle-minus", tone: "na", text: "Trading liquidity isn't checked — that data isn't available in this dataset." };
  }
  if (c.id === "gate_history") {
    const [years] = numsFrom(c.detail);
    if (tone === "ok") return { icon, tone, text: `${years ?? "Enough"} years of annual results are on record — enough history to judge consistency.` };
    if (tone === "bad") return { icon, tone, text: `Only ${years ?? "a few"} years of history — too new to fully trust the trend yet.` };
    return { icon, tone, text: "History depth wasn't assessed." };
  }
  if (c.id === "gate_freshness") {
    return { icon: "circle-minus", tone: "na", text: "How recent this data is hasn't been checked yet." };
  }
  return { icon, tone, text: c.detail || c.id.replace("gate_", "") };
}

function renderGates(gates: GateResult | undefined): string {
  if (!gates) return "";
  const dq = gates.disqualifiers;
  const rows = gates.checks.map((c) => {
    const g = gateSentence(c, dq);
    return `<div class="gate-row gate-row-${g.tone}"><i class="fas fa-${g.icon}"></i><span>${escapeHtml(g.text)}</span></div>`;
  }).join("");

  const notAssessedCount = dq?.not_assessed?.length || 0;
  const dqNote = notAssessedCount
    ? `<div class="dq-summary-plain">${notAssessedCount} additional background check${notAssessedCount > 1 ? "s" : ""} (things like auditor changes or related-party deals) couldn't be assessed — that information isn't disclosed for this stock.</div>`
    : "";

  return `
    <div class="gates-panel ${gates.passed ? "gates-pass" : "gates-fail"}" ${anim(260)}>
      <div class="gates-title">${gates.passed ? '<i class="fas fa-circle-check"></i> Clears the basic screening checks' : '<i class="fas fa-ban"></i> Fails a basic screening check'}</div>
      <div class="gate-rows">${rows}</div>
      ${dqNote}
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Per-investor rule sets                                              */
/* ------------------------------------------------------------------ */
export function renderInvestorAnalysis(data: InvestorAnalysis): string {
  const strat = strategyOf(data.id);
  if (data.error) {
    return `
      ${renderStockInfo(data)}
      <div class="empty-msg"><div style="font-size:2.2rem;">📭</div><p>Could not evaluate ${escapeHtml(strat.name)}.</p><p style="font-size:0.85rem;">${escapeHtml(data.detail || data.error)}</p></div>
      ${uiStyles()}`;
  }

  const activeRuleSetId = data.rule_sets[0]?.id;
  const grahamSets = [
    { id: "graham_defensive", name: "Defensive Investor" },
    { id: "graham_enterprising", name: "Enterprising Investor" },
  ];
  const ruleSetTabs = data.id === "graham"
    ? `<div class="ruleset-tabs">${grahamSets.map((rs) =>
        `<button class="ruleset-tab ${rs.id === activeRuleSetId ? "active" : ""}" data-graham-ruleset="${rs.id}">${rs.name}</button>`).join("")}</div>`
    : "";

  return `
    <div class="analysis-header" ${anim(0)}>
      <h2 class="analysis-title"><i class="fas ${strat.icon}" style="color:var(--primary);margin-right:.5rem;"></i>${escapeHtml(strat.name)}</h2>
      <p class="analysis-subtitle">${escapeHtml(strat.tag)}${data.track ? ` · ${escapeHtml(trackLabel(data.track))} track` : ""}</p>
    </div>
    ${renderStockInfo(data)}
    ${data.philosophy ? `<div class="philosophy-box" ${anim(60)}>${escapeHtml(data.philosophy)}</div>` : ""}
    ${data.note ? `<div class="note-box"><strong>Note:</strong> ${escapeHtml(data.note)}</div>` : ""}
    ${data.caveat ? `<div class="note-box"><strong>Caveat:</strong> ${escapeHtml(data.caveat)}</div>` : ""}
    ${ruleSetTabs}
    ${data.rule_sets.map((rs, i) => renderRuleSet(rs, 100 + i * 60)).join("")}
    ${dataFlagsFootnote(data.data_flags)}
    ${disclaimerBox()}
    ${uiStyles()}
  `;
}

function renderRuleSet(rs: RuleSet, delay: number): string {
  return `
    <div class="ruleset-block anim-in" style="animation-delay:${delay}ms">
      <div class="ruleset-header">
        <h3>${escapeHtml(rs.name || rs.id)}</h3>
        <div class="ruleset-stats">
          <span class="ruleset-passrate">${rs.rules_passed}/${rs.rules_evaluable} passed · ${rs.pass_rate}%</span>
          ${rs.rules_insufficient ? `<span class="ruleset-na">${rs.rules_insufficient} not assessed</span>` : ""}
        </div>
      </div>
      <table class="metrics-table">
        <thead><tr><th>Rule</th><th>What it tests</th><th>Value</th><th>Threshold</th><th>Status</th></tr></thead>
        <tbody>${rs.rules.map(renderRule).join("")}</tbody>
      </table>
    </div>`;
}

function renderRule(rule: RuleResult): string {
  let badge: string;
  if (rule.status === "pass") badge = `<span class="chip chip-green">Pass</span>`;
  else if (rule.status === "fail") badge = `<span class="chip chip-red">Fail</span>`;
  else badge = `<span class="chip chip-grey">Not assessed</span>`;
  const classification = rule.classification
    ? `<div class="rule-classification">→ ${escapeHtml(rule.classification)}${rule.matrix_score !== undefined ? ` (${rule.matrix_score}/10)` : ""}</div>` : "";
  const newTag = rule.added_in_v2 ? `<span class="new-tag">new</span>` : "";
  return `
    <tr>
      <td class="metric-rule"><div class="rule-name">${escapeHtml(rule.label)} ${newTag}</div>${rule.category ? `<div class="rule-cat">${escapeHtml(rule.category)}</div>` : ""}${classification}</td>
      <td class="metric-tests">${escapeHtml(rule.tests || "")}</td>
      <td>${formatRuleValue(rule.value)}</td>
      <td class="metric-threshold">${escapeHtml(rule.threshold || "—")}</td>
      <td>${badge}</td>
    </tr>`;
}

/* ------------------------------------------------------------------ */
/* Disqualifiers                                                       */
/* ------------------------------------------------------------------ */
export function renderDisqualifiers(data: DisqualifiersResponse): string {
  const hard = data.checks.filter((c) => c.severity === "hard");
  const soft = data.checks.filter((c) => c.severity === "soft");
  return `
    <div class="analysis-header" ${anim(0)}>
      <h2 class="analysis-title">Disqualifier Gate</h2>
      <p class="analysis-subtitle">A composite would rank a fraud in the top decile — manipulation makes reported numbers look better, not worse. These checks run before any scoring.</p>
    </div>
    ${renderStockInfo(data)}
    <div class="gate-banner ${data.gate_passed ? "gates-pass" : "gates-fail"}" ${anim(40)}>
      ${data.gate_passed ? '<i class="fas fa-circle-check"></i> Hard disqualifier gate cleared' : `<i class="fas fa-ban"></i> Blocked: ${data.hard_failures.map(escapeHtml).join(", ")}`}
    </div>
    <h3 class="section-title">Hard checks</h3>
    <div class="dq-grid">${hard.map((c, i) => renderDqCheck(c, i)).join("")}</div>
    <h3 class="section-title">Soft checks</h3>
    <div class="dq-grid">${soft.map((c, i) => renderDqCheck(c, i)).join("")}</div>
    ${uiStyles()}
  `;
}

function renderDqCheck(c: DisqualifierCheck, i: number): string {
  const chip = c.status === "failed" ? `<span class="chip chip-red">failed</span>`
    : c.status === "cleared" ? `<span class="chip chip-green">cleared</span>`
    : `<span class="chip chip-grey">not assessed</span>`;
  return `
    <div class="dq-card dq-${c.status} anim-in" style="animation-delay:${i * 40}ms">
      <div class="dq-card-head"><span class="dq-label">${escapeHtml(c.label || c.id)}</span>${chip}</div>
      ${c.detail ? `<div class="dq-detail">${escapeHtml(c.detail)}</div>` : ""}
      ${c.reason ? `<div class="dq-reason">${escapeHtml(c.reason)}</div>` : ""}
    </div>`;
}

/* ------------------------------------------------------------------ */
/* All scores                                                          */
/* ------------------------------------------------------------------ */
/* /score nests the two-track result under `tracks` (with its own `.tracks`
   and `.risk_overlay`); tolerate both shapes. Exported so main.ts can mount
   the same three gauges after renderAllScores() is inserted into the DOM. */
export function extractTracksAndRisk(data: AllScoresResponse): {
  dv?: TrackScore; qc?: TrackScore; risk?: RiskOverlay;
} {
  const tr: any = data.tracks || {};
  const trackObj: any = tr.tracks || tr;
  const risk = ((data as any).risk_overlay || tr.risk_overlay) as RiskOverlay | undefined;
  return {
    dv: trackObj["deep_value"] as TrackScore | undefined,
    qc: trackObj["quality_compounder"] as TrackScore | undefined,
    risk,
  };
}

export function renderAllScores(data: AllScoresResponse): string {
  const { dv, qc, risk } = extractTracksAndRisk(data);
  const donuts = [
    dv ? renderDonut("Deep Value", dv.composite, dv.band, "", false, 40, "gauge-as-deep_value") : "",
    qc ? renderDonut("Quality Compounder", qc.composite, qc.band, "", false, 100, "gauge-as-quality_compounder") : "",
    risk ? renderDonut("Risk Overlay", risk.score, risk.band, "", false, 160, "gauge-as-risk") : "",
  ].join("");

  const cards = Object.values(data.investors).map((inv, i) => {
    const s = strategyOf(inv.id);
    const slug = INVESTOR_SLUGS[inv.id] || inv.id;
    const setLines = inv.rule_sets.map((rs) =>
      `<div class="as-stat"><span>${escapeHtml(rs.name || rs.id)}</span><strong>${rs.rules_passed}/${rs.rules_evaluable} · ${rs.pass_rate}%</strong></div>`).join("");
    return `
      <div class="ref-card clickable-card anim-in" style="animation-delay:${i * 45}ms" role="button" tabindex="0" onclick="navigateToInvestorAnalysis('${slug}')">
        <div class="ref-card-top">
          <div class="ref-icon"><i class="fas ${s.icon}"></i></div>
          <div class="ref-titles"><div class="ref-title">${escapeHtml(s.name)}</div><div class="ref-sub">${escapeHtml(s.tag)}</div></div>
        </div>
        <div class="as-stats">${setLines}</div>
        <div class="ref-go">Rule-by-rule detail <i class="fas fa-arrow-right"></i></div>
      </div>`;
  }).join("");

  return `
    <div class="analysis-header" ${anim(0)}>
      <h2 class="analysis-title">All Scores Overview</h2>
      <p class="analysis-subtitle">Two-track composites plus each strategy's rule-set alignment</p>
    </div>
    ${renderStockInfo(data)}
    ${donuts ? `<div class="donut-row donut-row--mini">${donuts}</div>` : ""}
    <div class="ref-grid">${cards}</div>
    ${uiStyles()}
  `;
}

/* ------------------------------------------------------------------ */
/* Top stocks (reference card grid)                                    */
/* ------------------------------------------------------------------ */
const AVATAR_PALETTE = [
  ["#e7f7ee", "#16a34a"], ["#e8f0fe", "#3b82f6"],
  ["#eef2f6", "#475569"], ["#eafaf3", "#0d9488"], ["#f0eefe", "#6366f1"],
];
function avatarColours(ticker: string): [string, string] {
  let h = 0;
  for (let i = 0; i < ticker.length; i++) h = (h * 31 + ticker.charCodeAt(i)) & 0xffff;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length] as [string, string];
}

export function renderTopStocks(data: TopStocksResponse): string {
  const trackName = trackLabel(data.track);
  let refreshDate = "";
  if (data.cache_timestamp) {
    try {
      refreshDate = new Date(data.cache_timestamp).toLocaleString("en-IN", {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true,
      });
    } catch (_) { refreshDate = data.cache_timestamp; }
  }

  const header = `
    <div class="analysis-header ts-header" ${anim(0)}>
      <div>
        <h2 class="analysis-title">Top ${escapeHtml(trackName)} Stocks</h2>
        <p class="analysis-subtitle">Ranked by the ${escapeHtml(trackName)} composite (sector-relative percentiles)${refreshDate ? ` · updated ${refreshDate}` : ""}</p>
      </div>
      <div class="ts-actions">
        <button id="export-rankings-btn" class="export-btn" title="Download the full ranking of ALL stocks (both tracks) as Excel"><i class="fas fa-file-excel"></i> Export all</button>
        <button id="refresh-top-stocks-btn" class="refresh-btn"><i class="fas fa-rotate"></i> Refresh</button>
      </div>
    </div>`;

  if (!data.top_stocks || data.top_stocks.length === 0) {
    return `${header}<div class="empty-msg"><p>No ranked stocks yet — click Refresh to build the universe.</p></div>${uiStyles()}`;
  }

  const cards = data.top_stocks.map((s, i) => renderStockCard(s, i)).join("");
  const isDV = data.track === "deep_value";
  const trackFoot = `
    <div class="ts-track-foot">
      <span class="ts-track-label">Ranking track</span>
      <div class="ts-track-toggle">
        <button class="ts-track-btn ${isDV ? "active" : ""}" data-track="deep_value">Deep Value</button>
        <button class="ts-track-btn ${!isDV ? "active" : ""}" data-track="quality_compounder">Quality Compounder</button>
      </div>
    </div>`;
  return `${header}<div class="ref-grid ts-grid">${cards}</div>${trackFoot}${uiStyles()}`;
}

const BAND_LABELS: Record<string, string> = {
  strong: "Strong", watch: "Watch", reject: "Reject",
  possible_trap: "Trap", trap: "Trap", unscored: "Unscored",
};

function bandHex(key: "green" | "blue" | "red" | "grey"): string {
  return key === "green" ? "#22c55e" : key === "blue" ? "#3b82f6"
    : key === "red" ? "#ef4444" : "#94a3b8";
}

// Deterministic colour per sector for the second label bar (Trello-style tags).
function sectorHue(sector?: string): number {
  const s = (sector || "").trim();
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

// Trello-inspired ranking card: colour label bars up top, bold title, a large
// composite score block, then a footer of icon "badges" and a member avatar.
function renderStockCard(s: TopStock, i: number): string {
  const [bg, fg] = avatarColours(s.ticker);
  const key = bandKey(s.band_colour);
  const scoreCls = key === "green" ? "tsc-score-green" : key === "blue" ? "tsc-score-blue"
    : key === "red" ? "tsc-score-red" : "tsc-score-grey";
  const bandLabel = BAND_LABELS[s.band] || (s.band ? s.band.replace(/_/g, " ") : "—");
  const hue = sectorHue(s.sector);
  const gate = s.gate_passed;
  const sector = s.sector || "—";
  return `
    <div class="tsc stock-row anim-in" style="animation-delay:${Math.min(i, 12) * 35}ms" data-ticker="${escapeHtml(s.ticker)}" role="button" tabindex="0" aria-label="${escapeHtml(s.ticker)} — ${escapeHtml(bandLabel)}, score ${s.composite.toFixed(1)}">
      <span class="tsc-rank">#${i + 1}</span>
      <div class="tsc-labels">
        <span class="tsc-label" style="background:${bandHex(key)}" title="Band: ${escapeHtml(bandLabel)}"></span>
        <span class="tsc-label" style="background:hsl(${hue} 68% 55%)" title="Sector: ${escapeHtml(sector)}"></span>
      </div>
      <div class="tsc-main">
        <div class="tsc-headline">
          <div class="tsc-title">${escapeHtml(s.ticker)}</div>
          <div class="tsc-sub">${escapeHtml(s.company_name)}</div>
        </div>
        <div class="tsc-score ${scoreCls}" title="Composite score (out of 10)">
          <span class="tsc-score-num">${s.composite.toFixed(1)}</span>
          <span class="tsc-score-band">${escapeHtml(bandLabel)}</span>
        </div>
      </div>
      <div class="tsc-foot">
        <div class="tsc-badges">
          <span class="tsc-badge ${gate ? "badge-green" : "badge-grey"}" title="Screening gate ${gate ? "passed" : "not passed"}"><i class="fas fa-${gate ? "circle-check" : "circle-minus"}"></i> Gate</span>
          <span class="tsc-badge tsc-badge-soft" title="Sector: ${escapeHtml(sector)}"><i class="fas fa-tag"></i> ${escapeHtml(sector)}</span>
          <span class="tsc-badge tsc-badge-plain" title="Market cap"><i class="fas fa-coins"></i> ${formatCurrency(s.market_cap_cr)}</span>
        </div>
        <div class="tsc-avatar" style="background:${bg};color:${fg};" title="${escapeHtml(s.ticker)}">${escapeHtml(s.ticker.slice(0, 2))}</div>
      </div>
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Portfolio rules (sell + sizing)                                     */
/* ------------------------------------------------------------------ */
export function renderPortfolioRules(sellRules: SellRule[], sizing: PositionSizing): string {
  const sellCards = sellRules.map((r, i) => {
    const body = Object.entries(r)
      .filter(([k]) => !["id", "label"].includes(k))
      .map(([k, v]) => `<div class="kv"><span class="k">${escapeHtml(k.replace(/_/g, " "))}</span><span class="v">${escapeHtml(typeof v === "object" ? JSON.stringify(v) : v)}</span></div>`)
      .join("");
    return `<div class="ref-card anim-in" style="animation-delay:${i * 40}ms"><div class="sell-title">${escapeHtml(r.label || r.id)}</div>${body}</div>`;
  }).join("");

  const sizingTables = TRACK_ORDER.filter((t) => sizing[t]).map((t) => {
    const rows = Object.entries(sizing[t])
      .map(([k, v]) => `<tr><td>${escapeHtml(k.replace(/_/g, " "))}</td><td>${escapeHtml(typeof v === "object" ? JSON.stringify(v) : v)}</td></tr>`).join("");
    return `<div class="ref-card"><h4 class="sizing-h">${escapeHtml(trackLabel(t))}</h4><table class="metrics-table compact"><tbody>${rows}</tbody></table></div>`;
  }).join("");

  return `
    <div class="analysis-header" ${anim(0)}>
      <h2 class="analysis-title">Sell Rules & Position Sizing</h2>
      <p class="analysis-subtitle">Entry rules without exit rules are half a framework. These are portfolio-level, independent of the screener.</p>
    </div>
    <h3 class="section-title">Sell rules</h3>
    <div class="ref-grid">${sellCards}</div>
    <h3 class="section-title">Position sizing</h3>
    <div class="sizing-grid">${sizingTables}</div>
    ${disclaimerBox()}
    ${uiStyles()}
  `;
}

/* ------------------------------------------------------------------ */
/* misc                                                                */
/* ------------------------------------------------------------------ */
function trackLabel(track?: string): string {
  if (track === "deep_value") return "Deep Value";
  if (track === "quality_compounder") return "Quality Compounder";
  return track || "";
}

export function renderLoginView(): string {
  return `
    <div class="login-container" style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;padding:2rem;">
      <div class="ref-card" style="max-width:460px;width:100%;text-align:center;padding:2.5rem;">
        <div style="font-size:2.5rem;margin-bottom:1rem;">🔒</div>
        <h2 style="color:var(--text-primary);margin-bottom:0.75rem;">Sign in to view Top Stocks</h2>
        <p style="color:var(--text-secondary);line-height:1.6;">Sign in with Google to access the <strong>Top Stocks</strong> leaderboards.</p>
        <div style="margin:1.75rem 0 0;">
          <button id="google-signin-btn" class="google-signin-btn" style="background:white;color:#3c4043;border:1px solid #dadce0;padding:0.75rem 1.5rem;border-radius:0.6rem;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:0.75rem;font-size:1rem;box-shadow:var(--shadow-sm);">
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path></svg>
            <span>Sign in with Google</span>
          </button>
        </div>
      </div>
    </div>${uiStyles()}`;
}

/* ------------------------------------------------------------------ */
/* Scoped styles — white minimalist                                    */
/* ------------------------------------------------------------------ */
function uiStyles(): string {
  return `<style>
    @keyframes fadeInUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
    .anim-in { opacity:0; animation: fadeInUp .5s cubic-bezier(.22,.61,.36,1) forwards; }
    @media (prefers-reduced-motion: reduce){ .anim-in{opacity:1;animation:none;} }

    .analysis-header{ margin-bottom:1.1rem; }
    .analysis-title{ font-size:2.3rem; font-weight:800; letter-spacing:-.02em; color:var(--text-primary); }
    .analysis-subtitle{ color:var(--text-secondary); margin-top:.4rem; max-width:70ch; font-size:1.1rem; }
    .ts-header{ display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; flex-wrap:wrap; }
    .section-title{ font-size:1.1rem; font-weight:700; margin:1.5rem 0 .85rem; color:var(--text-primary); }

    /* stock info */
    .stock-info-card{ display:flex; justify-content:space-between; align-items:center; gap:1rem; flex-wrap:wrap;
      background:var(--card-bg); border:none; border-radius:18px; padding:1rem 1.25rem; box-shadow:var(--neu-raised-sm); margin-bottom:1.1rem; }
    .stock-info-main{ display:flex; align-items:center; gap:.85rem; }
    .stock-avatar{ width:52px; height:52px; border-radius:50%; background:#eef2f6; color:#334155; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:1.05rem; }
    .stock-info-name{ font-weight:700; font-size:1.22rem; color:var(--text-primary); }
    .stock-info-ticker{ font-size:.92rem; color:var(--text-secondary); }
    .stock-info-meta{ display:flex; gap:1.75rem; }
    .stock-info-meta > div{ display:flex; flex-direction:column; }
    .mini-label{ font-size:.75rem; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); }
    .mini-value{ font-weight:700; color:var(--text-primary); font-size:1.05rem; }

    /* donuts */
    .donut-row{ display:grid; grid-template-columns:repeat(3,1fr); gap:1.1rem; margin:.25rem 0 1.25rem; }
    @media (max-width:820px){ .donut-row{ grid-template-columns:1fr; } }
    .donut-row--mini{ margin-bottom:1.5rem; }
    .donut-card{ position:relative; overflow:hidden; background:var(--card-bg); border:none; border-radius:20px; padding:1.5rem 1rem 1.15rem; text-align:center; box-shadow:var(--neu-raised-sm); transition:box-shadow .2s, transform .2s; }
    .donut-card:hover{ box-shadow:var(--neu-raised); transform:translateY(-2px); }
    .donut-rec{ position:absolute; top:.75rem; right:.75rem; background:var(--success-soft); color:var(--success-dark); font-size:.62rem; font-weight:800; text-transform:uppercase; letter-spacing:.03em; padding:.2rem .5rem; border-radius:999px; }
    .donut-wrap{ position:relative; width:100%; margin:0 auto .3rem; }
    .donut-chart-mount{ width:100%; height:170px; margin:0 auto; }
    .donut-chart-mount .apexcharts-canvas{ margin:0 auto; }
    .donut-max{ font-size:.82rem; color:var(--text-muted); margin-top:-.6rem; }
    .donut-label{ font-weight:700; font-size:1.3rem; color:var(--text-primary); margin:.5rem 0 .35rem; }
    .donut-sub{ font-size:.86rem; color:var(--text-secondary); margin-top:.5rem; min-height:1.1rem; }

    /* score-at-a-glance combined chart */
    .glance-panel{ text-align:center; padding:1.3rem 1.35rem 1.5rem; margin-bottom:1.5rem; }
    .glance-title{ margin-bottom:.9rem; }
    .glance-rings{ display:flex; justify-content:center; align-items:flex-start; gap:2.5rem; flex-wrap:wrap; }
    .glance-ring-item{ display:flex; flex-direction:column; align-items:center; gap:.5rem; }
    .glance-ring-mount{ width:160px; height:170px; }
    .glance-ring-label{ font-weight:700; font-size:.95rem; color:var(--text-primary); }
    @media (max-width:560px){ .glance-rings{ gap:1.25rem; } .glance-ring-mount{ width:130px; height:150px; } }

    /* dimension bar chart (per track, above the detailed expandable rows) */
    .dim-bar-chart-mount{ margin:.2rem 0 1.1rem; }

    .band-chip{ display:inline-block; font-size:.74rem; font-weight:800; padding:.26rem .75rem; border-radius:999px; text-transform:capitalize; letter-spacing:.02em; box-shadow:0 2px 6px rgba(16,24,40,.14); }
    .band-green{ background:#16a34a; color:#fff; }
    .band-blue{ background:#2563eb; color:#fff; }
    .band-red{ background:#dc2626; color:#fff; }
    .band-grey{ background:#64748b; color:#fff; }
    .rec-tag{ background:var(--success-soft); color:var(--success-dark); font-size:.62rem; font-weight:800; padding:.15rem .5rem; border-radius:999px; text-transform:uppercase; letter-spacing:.03em; vertical-align:middle; }

    /* panels */
    .panel{ position:relative; overflow:hidden; background:var(--card-bg); border:none; border-radius:20px; padding:1.4rem 1.35rem 1.25rem; box-shadow:var(--neu-raised-sm); margin-bottom:1.1rem; }
    .panel-strip{ position:absolute; top:0; left:0; right:0; height:6px; }
    .strip-green{ background:#16a34a; } .strip-blue{ background:#2563eb; }
    .strip-red{ background:#dc2626; } .strip-grey{ background:#94a3b8; }
    .panel-dims-label{ font-size:.72rem; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:1.25rem 0 .5rem; }

    /* track panels with colored header band */
    .panel-track{ padding:0; }
    .panel-band{ display:flex; justify-content:space-between; align-items:center; gap:1rem; flex-wrap:wrap; padding:1.05rem 1.35rem; }
    .band-bg-green{ background:#e7f7ee; border-bottom:1px solid #d3eede; }
    .band-bg-blue{ background:#e6effe; border-bottom:1px solid #d8e4fb; }
    .band-bg-red{ background:#fdecec; border-bottom:1px solid #f6d5d5; }
    .band-bg-grey{ background:#eef1f5; border-bottom:1px solid #e2e7ee; }
    .panel-band-right{ display:flex; align-items:center; gap:.7rem; }
    .panel-band-score{ font-size:1.6rem; font-weight:800; color:#fff; padding:.3rem .95rem; border-radius:12px; line-height:1.25; box-shadow:0 3px 8px rgba(16,24,40,.18); }
    .panel-body{ padding:1.15rem 1.35rem 1.3rem; }

    /* tinted gauge cards */
    .tint-green{ background:#f2fbf6; }
    .tint-blue{ background:#f0f5ff; }
    .tint-red{ background:#fdf3f3; }
    .tint-grey{ background:#f4f6f9; }
    .panel-head{ display:flex; justify-content:space-between; align-items:baseline; gap:1rem; flex-wrap:wrap; margin-bottom:.5rem; }
    .panel-title{ font-size:1.3rem; font-weight:800; color:var(--text-primary); }
    .panel-portfolio{ font-size:.78rem; color:var(--text-secondary); }
    .trap-banner{ display:flex; gap:.5rem; align-items:center; background:var(--danger-soft); color:#b91c1c; border-radius:10px; padding:.6rem .8rem; font-size:.85rem; font-weight:600; margin:.5rem 0 .75rem; }

    /* dimensions */
    .dim-list{ display:flex; flex-direction:column; gap:.15rem; }
    .dim{ padding:.55rem 0; border-bottom:1px solid var(--border-light); }
    .dim:last-child{ border-bottom:none; }
    .dim-head{ display:flex; justify-content:space-between; align-items:center; cursor:pointer; }
    .dim-info{ display:flex; flex-direction:column; }
    .dim-label{ font-weight:600; color:var(--text-primary); font-size:1.08rem; }
    .dim-weight{ font-size:.82rem; color:var(--text-muted); }
    .dim-right{ display:flex; align-items:center; gap:.6rem; }
    .dim-score{ font-weight:800; font-size:1.3rem; min-width:3.4rem; text-align:center; padding:.32rem .65rem; border-radius:11px; line-height:1; box-shadow:0 2px 6px rgba(16,24,40,.14); }
    .dim-score-green{ background:#16a34a; color:#fff; }
    .dim-score-blue{ background:#2563eb; color:#fff; }
    .dim-score-low{ background:#64748b; color:#fff; }
    .dim-score-null{ background:transparent; color:var(--text-muted); box-shadow:none; border:1.5px dashed var(--border-color); }
    .expand-icon{ color:var(--text-muted); font-size:.75rem; transition:transform .2s; }
    .metrics-table-header.expanded .expand-icon, .dim-head.expanded .expand-icon{ transform:rotate(180deg); }
    .dim-bar{ height:7px; background:#eef1f5; border-radius:99px; overflow:hidden; margin:.5rem 0 .1rem; }
    .dim-bar-fill{ height:100%; border-radius:99px; transition:width .8s cubic-bezier(.22,.61,.36,1); }
    .bar-green{ background:#16a34a; } .bar-blue{ background:#3b82f6; } .bar-grey{ background:#cbd2dc; }
    .metrics-table-content{ overflow:hidden; transition:max-height .3s ease; }
    .metrics-table-content.collapsed{ max-height:0; }
    .metrics-table-content.expanded{ max-height:900px; }

    /* strategy showcase (combined, both tracks) — reuses .tsc card language */
    .strategy-showcase-grid{ margin-bottom:1.75rem; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:1.15rem; }
    .strategy-tsc{ cursor:pointer; padding:1.2rem 1.35rem 1.1rem; }
    .tsc-investor{ font-size:.82rem; font-weight:700; color:var(--primary); margin-top:.15rem; }
    .tsc-score-mini{ flex:none; font-size:1.1rem; min-width:3.6rem; }
    .tsc-arrow{ display:inline-flex; align-items:center; gap:.4rem; color:var(--text-muted); font-size:.82rem; font-weight:700; transition:transform .15s, color .15s; margin-left:auto; }
    .strategy-tsc:hover .tsc-arrow{ color:var(--primary); transform:translateX(3px); }
    .strategy-tsc:hover{ box-shadow:var(--neu-raised); transform:translateY(-4px); }

    /* reference cards (top stocks, all scores) */
    .ref-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(250px,1fr)); gap:1rem; }
    .ts-grid{ grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); }
    .ref-card{ background:var(--card-bg); border:none; border-radius:18px; padding:1.1rem 1.2rem; box-shadow:var(--neu-raised-sm); transition:box-shadow .2s, transform .2s; }
    .ref-card.clickable-card, .ref-card.stock-row{ cursor:pointer; }
    .ref-card.clickable-card:hover, .ref-card.stock-row:hover{ box-shadow:var(--neu-raised); transform:translateY(-3px); }
    .ref-card-top{ display:flex; align-items:center; gap:.75rem; }
    .ref-icon{ width:44px; height:44px; border-radius:50%; background:#eef2f6; color:#334155; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:.9rem; flex-shrink:0; }
    .ref-titles{ flex:1; min-width:0; }
    .ref-title{ font-weight:800; color:var(--text-primary); font-size:1rem; }
    .ref-sub{ font-size:.8rem; color:var(--text-secondary); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .rank-badge{ font-size:.72rem; font-weight:700; color:var(--text-muted); background:var(--bg-tertiary); box-shadow:var(--neu-inset-sm); padding:.15rem .5rem; border-radius:999px; }
    .ref-card-bottom{ display:flex; align-items:center; justify-content:space-between; margin-top:1.1rem; }
    .ref-value{ font-size:1.35rem; font-weight:800; color:var(--text-primary); }
    .pill{ display:inline-flex; align-items:center; gap:.25rem; font-size:.8rem; font-weight:800; padding:.3rem .6rem; border-radius:999px; }
    .pill-green{ background:var(--success-soft); color:var(--success-dark); }
    .pill-red{ background:var(--danger-soft); color:#b91c1c; }
    .pill-grey{ background:#eef1f5; color:#64748b; }
    .as-stats{ margin-top:.85rem; display:flex; flex-direction:column; gap:.2rem; }
    .as-stat{ display:flex; justify-content:space-between; font-size:.8rem; color:var(--text-secondary); }
    .as-stat strong{ color:var(--text-primary); }
    .ref-go{ margin-top:.85rem; font-size:.78rem; font-weight:700; color:var(--primary); display:flex; align-items:center; gap:.35rem; }

    /* Trello-inspired ranking cards (top stocks) */
    .ts-grid{ grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:1.1rem; }
    .tsc{ position:relative; background:var(--card-bg); border:none; border-radius:18px; padding:1.05rem 1.25rem 1rem; box-shadow:var(--neu-raised-sm); cursor:pointer; display:flex; flex-direction:column; gap:.6rem; transition:box-shadow .2s, transform .16s; }
    .tsc:hover{ box-shadow:var(--neu-raised); transform:translateY(-4px); }
    .tsc:focus-visible{ outline:2px solid var(--primary); outline-offset:2px; }
    .tsc-labels{ display:flex; gap:.4rem; }
    .tsc-label{ height:9px; width:46px; border-radius:6px; opacity:.92; }
    .tsc-rank{ position:absolute; top:.85rem; right:1rem; font-size:.74rem; font-weight:800; color:var(--text-muted); background:var(--bg-tertiary); box-shadow:var(--neu-inset-sm); padding:.14rem .55rem; border-radius:999px; }
    .tsc-main{ display:flex; align-items:center; justify-content:space-between; gap:.9rem; }
    .tsc-headline{ min-width:0; }
    .tsc-title{ font-weight:800; font-size:1.4rem; letter-spacing:.2px; color:var(--text-primary); line-height:1.1; }
    .tsc-sub{ font-size:.9rem; color:var(--text-secondary); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; margin-top:.15rem; }
    .tsc-score{ flex:none; text-align:center; border-radius:14px; padding:.5rem .75rem; min-width:78px; display:flex; flex-direction:column; align-items:center; gap:.1rem; }
    .tsc-score-num{ font-size:2.1rem; font-weight:800; line-height:1; }
    .tsc-score-band{ font-size:.66rem; font-weight:800; text-transform:uppercase; letter-spacing:.04em; opacity:.9; }
    .tsc-score-green{ background:var(--success-soft); color:var(--success-dark); }
    .tsc-score-blue{ background:var(--info-soft); color:#1d4ed8; }
    .tsc-score-red{ background:var(--danger-soft); color:#b91c1c; }
    .tsc-score-grey{ background:#eef1f5; color:#64748b; }
    .tsc-foot{ display:flex; align-items:center; justify-content:space-between; gap:.5rem; margin-top:.2rem; }
    .tsc-badges{ display:flex; flex-wrap:wrap; gap:.4rem; align-items:center; min-width:0; }
    .tsc-badge{ display:inline-flex; align-items:center; gap:.32rem; font-size:.78rem; font-weight:800; padding:.3rem .6rem; border-radius:8px; line-height:1; white-space:nowrap; }
    .tsc-badge i{ font-size:.76rem; opacity:.9; }
    .tsc-badge-soft{ background:var(--bg-primary); color:var(--text-secondary); max-width:160px; overflow:hidden; text-overflow:ellipsis; }
    .tsc-badge-plain{ background:transparent; color:var(--text-secondary); padding:.3rem .2rem; font-weight:700; }
    .badge-green{ background:var(--success-soft); color:var(--success-dark); }
    .badge-red{ background:var(--danger-soft); color:#b91c1c; }
    .badge-grey{ background:#eef1f5; color:#64748b; }
    .tsc-avatar{ width:42px; height:42px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:.82rem; flex:none; box-shadow:0 0 0 2px var(--card-bg), 0 1px 3px rgba(0,0,0,.18); }

    /* bottom track selector (after the card view) */
    .ts-track-foot{ display:flex; flex-direction:column; align-items:center; gap:.7rem; margin:2.25rem 0 .5rem; }
    .ts-track-label{ font-size:.74rem; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); font-weight:800; }
    .ts-track-toggle{ display:inline-flex; gap:.35rem; background:var(--bg-primary); border:none; border-radius:14px; padding:.4rem; box-shadow:var(--neu-inset-sm); }
    .ts-track-btn{ border:none; background:transparent; color:var(--text-secondary); font-weight:800; font-size:.98rem; padding:.6rem 1.5rem; border-radius:10px; cursor:pointer; transition:background .15s, color .15s; }
    .ts-track-btn:hover{ color:var(--text-primary); }
    .ts-track-btn.active{ background:var(--primary); color:#fff; box-shadow:var(--neu-raised-sm); }

    /* gates */
    .gates-panel, .gate-banner{ border:none; border-radius:18px; padding:1rem 1.1rem; margin:0 0 1.1rem; box-shadow:var(--neu-raised-sm); background:var(--card-bg); }
    .gates-pass{ border-left:4px solid #16a34a; }
    .gates-fail{ border-left:4px solid #ef4444; }
    .gates-title{ font-weight:800; margin-bottom:.55rem; display:flex; align-items:center; gap:.45rem; color:var(--text-primary); }
    .gates-pass .gates-title i{ color:#16a34a; } .gates-fail .gates-title i{ color:#ef4444; }
    .gate-row{ display:flex; align-items:flex-start; gap:.65rem; font-size:.92rem; padding:.4rem 0; color:var(--text-secondary); line-height:1.45; }
    .gate-row i{ margin-top:.15rem; flex:none; }
    .gate-row-ok i{ color:#16a34a; } .gate-row-ok span{ color:var(--text-primary); }
    .gate-row-bad i{ color:#dc2626; } .gate-row-bad span{ color:var(--text-primary); font-weight:600; }
    .gate-row-na i{ color:var(--text-muted); }
    .gate-banner{ font-weight:700; display:flex; align-items:center; gap:.5rem; }
    .dq-summary-plain{ margin-top:.7rem; padding-top:.7rem; border-top:1px solid var(--border-light); font-size:.85rem; color:var(--text-muted); line-height:1.5; }

    /* chips */
    .chip{ font-size:.72rem; font-weight:800; padding:.16rem .5rem; border-radius:6px; white-space:nowrap; }
    .chip-green{ background:var(--success-soft); color:var(--success-dark); }
    .chip-red{ background:var(--danger-soft); color:#b91c1c; }
    .chip-grey{ background:#eef1f5; color:#64748b; }

    /* tables */
    .metrics-table{ width:100%; border-collapse:collapse; margin-top:.6rem; }
    .metrics-table th{ text-align:left; padding:.55rem .75rem; font-size:.92rem; text-transform:uppercase; letter-spacing:.03em; color:var(--text-muted); border-bottom:1px solid var(--border-color); }
    .metrics-table td{ padding:.55rem .75rem; border-bottom:1px solid var(--border-light); font-size:1rem; vertical-align:top; color:var(--text-secondary); }
    .metrics-table td.metric-rule, .metrics-table .rule-name{ color:var(--text-primary); font-weight:600; }
    .metrics-table.compact td, .metrics-table.compact th{ padding:.4rem .6rem; font-size:.86rem; }
    .rule-cat{ font-size:.68rem; color:var(--text-muted); text-transform:capitalize; }
    .rule-classification{ font-size:.74rem; color:var(--primary); margin-top:.2rem; }
    .new-tag{ background:#3b82f6; color:#fff; font-size:.58rem; padding:.05rem .35rem; border-radius:999px; text-transform:uppercase; font-weight:800; }

    /* investor detail */
    .philosophy-box{ background:var(--bg-primary); border:none; border-radius:16px; padding:1rem 1.2rem; margin:0 0 1rem; color:var(--text-secondary); line-height:1.6; font-size:.92rem; box-shadow:var(--neu-inset-sm); }
    .note-box{ font-size:.85rem; color:var(--text-secondary); margin:.4rem 0; }
    .ruleset-tabs{ display:flex; gap:.5rem; margin:1rem 0; flex-wrap:wrap; }
    .ruleset-tab{ padding:.5rem 1rem; border:none; background:var(--card-bg); border-radius:10px; cursor:pointer; font-weight:700; font-size:.88rem; color:var(--text-secondary); box-shadow:var(--neu-raised-sm); }
    .ruleset-tab.active{ background:var(--primary); color:#fff; box-shadow:var(--neu-inset-sm); }
    .ruleset-block{ background:var(--card-bg); border:none; border-radius:18px; padding:1rem 1.2rem; margin:1rem 0; box-shadow:var(--neu-raised-sm); }
    .ruleset-header{ display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:.5rem; }
    .ruleset-header h3{ font-size:1.05rem; color:var(--text-primary); }
    .ruleset-passrate{ font-weight:700; color:var(--text-primary); } .ruleset-na{ color:var(--text-muted); font-size:.8rem; margin-left:.6rem; }

    /* disqualifiers */
    .dq-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:.75rem; }
    .dq-card{ border:none; border-left:4px solid #cbd2dc; border-radius:12px; padding:.8rem .95rem; background:var(--card-bg); box-shadow:var(--neu-raised-sm); }
    .dq-card.dq-failed{ border-left-color:#ef4444; } .dq-card.dq-cleared{ border-left-color:#16a34a; } .dq-card.dq-not_assessed{ border-left-color:#cbd2dc; }
    .dq-card-head{ display:flex; justify-content:space-between; align-items:center; gap:.5rem; }
    .dq-label{ font-weight:700; color:var(--text-primary); }
    .dq-detail{ font-size:.8rem; margin-top:.3rem; color:var(--text-secondary); } .dq-reason{ font-size:.75rem; color:var(--text-muted); margin-top:.3rem; }

    /* notes */
    .inline-note{ font-size:.82rem; color:var(--text-secondary); background:var(--info-soft); border-radius:10px; padding:.6rem .8rem; margin:0 0 1rem; }
    .disclaimer{ display:flex; gap:.6rem; align-items:flex-start; font-size:.8rem; color:var(--text-secondary); background:var(--bg-primary); border:none; border-radius:14px; padding:.75rem .9rem; margin:1.25rem 0 0; box-shadow:var(--neu-inset-sm); }
    .disclaimer i{ color:var(--text-muted); margin-top:.15rem; }
    .data-flags{ margin-top:1rem; font-size:.78rem; color:var(--text-muted); }
    .data-flags-title{ font-weight:700; margin-bottom:.25rem; color:var(--text-secondary); }
    .data-flags ul{ margin:0; padding-left:1.1rem; }

    /* portfolio */
    .kv{ display:flex; justify-content:space-between; gap:.5rem; font-size:.78rem; padding:.12rem 0; }
    .kv .k{ color:var(--text-secondary); text-transform:capitalize; } .kv .v{ color:var(--text-primary); font-weight:600; }
    .sell-title{ font-weight:800; margin-bottom:.5rem; color:var(--text-primary); }
    .sizing-grid{ display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
    @media (max-width:800px){ .sizing-grid{ grid-template-columns:1fr; } }
    .sizing-h{ margin-bottom:.4rem; color:var(--text-primary); }

    .refresh-btn{ background:var(--primary); color:#fff; border:none; padding:.6rem 1.1rem; border-radius:12px; font-weight:700; cursor:pointer; display:inline-flex; gap:.45rem; align-items:center; box-shadow:var(--neu-raised-sm); transition:transform .12s, box-shadow .2s; }
    .refresh-btn:hover{ transform:translateY(-1px); box-shadow:var(--neu-raised); }
    .ts-actions{ display:inline-flex; gap:.6rem; align-items:center; flex-wrap:wrap; }
    .export-btn{ background:var(--success-dark, #4f7062); color:#fff; border:none; padding:.6rem 1.1rem; border-radius:12px; font-weight:700; cursor:pointer; display:inline-flex; gap:.45rem; align-items:center; box-shadow:var(--neu-raised-sm); transition:transform .12s, box-shadow .2s; }
    .export-btn:hover{ transform:translateY(-1px); box-shadow:var(--neu-raised); }
    .export-btn:disabled{ opacity:.6; cursor:not-allowed; transform:none; }
    .empty-msg{ text-align:center; padding:2.5rem; color:var(--text-secondary); }
  </style>`;
}
